import React, { ReactElement, useEffect } from 'react';
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Col,
  FormGroup,
  Input,
  Label,
  LoaderButton,
  Row, Text,
  Textarea,
} from 'idsk-react-components';
import { ConfirmModal } from 'slovlex-idsk-react-components';
import ClassType from '../../../model/pripomienka/ClassType';
import SavePripomienka from '../../../model/pripomienka/SavePripomienka';
import { SafeParameterSetter } from '../../../types/SafeParameterSetter';
import { useUserContext } from '../../../context/UserContext';
import './editPripomienkaForm.scss';
import ZasadnaFormComponent from '../../AddPripomienka/component/ZasadnaFormComponent';
import PripomienkaDetail from '../../../model/pripomienka/PripomienkaDetail';
import TypPripomienky from '../../../model/pripomienka/TypPripomienky';

interface Props {
  onSubmit: () => void;
  onReset: () => void;
  getFieldErrorMessage: (fieldName: string) => string;
  onUpdateField: SafeParameterSetter<SavePripomienka>;
  saveLoading?: boolean;
  pripomienkaDetail: PripomienkaDetail;
  updatedPripomienka: SavePripomienka;
}

const EditPripomienkaForm = ({
  onSubmit,
  onReset,
  getFieldErrorMessage,
  onUpdateField,
  saveLoading,
  pripomienkaDetail,
  updatedPripomienka,
}: Props): ReactElement => {
  const { pouzivatel } = useUserContext();
  const isPovinnyPripomienkovatel = pouzivatel && pouzivatel.isPovinnyPripomienkovatel;
  const isDalsiPripomienkovatel = pouzivatel && pouzivatel.isDalsiPripomienkovatel;

  useEffect(() => {
    onUpdateField('text', pripomienkaDetail.text);
    onUpdateField('zasadna', pripomienkaDetail.typ === TypPripomienky.ZASADNA);
    if (pripomienkaDetail.pripomienkaK === 'Celému materiálu') {
      onUpdateField('celyMaterial', true);
    } else {
      onUpdateField('ine', pripomienkaDetail.pripomienkaK);
    }
    // with onUpdateField get warning Maximum update depth exceeded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pripomienkaDetail]);

  const onCelyMaterialClick = (checked: boolean): void => {
    onUpdateField('celyMaterial', checked);

    if (checked) {
      onUpdateField('clanok', '');
      onUpdateField('paragraf', '');
      onUpdateField('odsek', '');
      onUpdateField('ine', '');
    }
  };

  const labelClassName: string = updatedPripomienka.celyMaterial ? 'pripomienkak-label-disabled' : null;

  return pripomienkaDetail && (
    <form className="edit-pripomienka-form">
      <FormGroup error={!!getFieldErrorMessage('pripomienkaK')}>
        <Text className="pripomienka-k">Pripomienka k</Text>
        <span className="idsk-hint">Zadajte označenie ustanovenia, ktorého sa pripomienka
          týka. Vyplnenie minimálne jednej z možností je povinné.
        </span>
        <Text className="idsk-error-message">{getFieldErrorMessage('pripomienkaK')}</Text>
      </FormGroup>
      <Row>
        <Col width="one-half" className="fields-column">
          <Col width="one-third">
            <FormGroup error={!!getFieldErrorMessage('clanok')}>
              <Label className={labelClassName} htmlFor="clanok">Článku</Label>
              <Input
                id="clanok"
                rows={1}
                type="text"
                maxLength="20"
                name="clanok"
                className="pripomienkak-input"
                disabled={updatedPripomienka.celyMaterial}
                value={updatedPripomienka.clanok}
                error={getFieldErrorMessage('clanok')}
                onChange={({ target }) => onUpdateField('clanok', target.value)}
              />
            </FormGroup>
          </Col>
          <Col width="one-third">
            <FormGroup error={!!getFieldErrorMessage('paragraf')}>
              <Label className={labelClassName} htmlFor="paragraf">Paragrafu</Label>
              <Input
                id="paragraf"
                rows={1}
                type="text"
                maxLength="20"
                name="paragraf"
                className="pripomienkak-input"
                disabled={updatedPripomienka.celyMaterial}
                value={updatedPripomienka.paragraf}
                error={getFieldErrorMessage('paragraf')}
                onChange={({ target }) => onUpdateField('paragraf', target.value)}
              />
            </FormGroup>
          </Col>
          <Col width="one-third">
            <FormGroup error={!!getFieldErrorMessage('odsek')}>
              <Label className={labelClassName} htmlFor="odsek">Odseku</Label>
              <Input
                id="odsek"
                rows={1}
                type="text"
                maxLength="20"
                name="odsek"
                className="pripomienkak-input"
                disabled={updatedPripomienka.celyMaterial}
                value={updatedPripomienka.odsek}
                error={getFieldErrorMessage('odsek')}
                onChange={({ target }) => onUpdateField('odsek', target.value)}
              />
            </FormGroup>
          </Col>
        </Col>
        <Col width="one-half" className="fields-column">
          <Col width="one-third">
            <CheckboxGroup className="edit-pripomienka-checkbox-wrapper">
              <Checkbox
                className="edit-pripomienka-checkbox"
                id="celyMaterial"
                onChange={({ target }) => onCelyMaterialClick(target.checked)}
                checked={updatedPripomienka.celyMaterial}
              >
                Celému materiálu
              </Checkbox>
            </CheckboxGroup>
          </Col>
          <Col width="two-thirds">
            <FormGroup error={!!getFieldErrorMessage('ine')}>
              <Label className={labelClassName} htmlFor="ine">Iné</Label>
              <Input
                id="ine"
                rows={1}
                maxLength="120"
                name="ine"
                type="text"
                className="pripomienkak-input"
                disabled={updatedPripomienka.celyMaterial}
                value={updatedPripomienka.ine}
                error={getFieldErrorMessage('ine')}
                onChange={({ target }) => onUpdateField('ine', target.value)}
              />
            </FormGroup>
          </Col>
        </Col>
      </Row>
      <FormGroup error={!!getFieldErrorMessage('text')}>
        <Label htmlFor="text">Text pripomienky</Label>
        <Textarea
          id="text"
          rows={15}
          maxLength={65000}
          name="text"
          error={(getFieldErrorMessage('text'))}
          value={updatedPripomienka.text}
          onChange={({ target }) => onUpdateField('text', target.value)}
        />
      </FormGroup>
      <Row>
        <Col width="one-half" className="zasadna-container">
          {(isPovinnyPripomienkovatel || isDalsiPripomienkovatel) && (
            <ZasadnaFormComponent
              classType={ClassType.PRIPOMIENKA}
              updateField={onUpdateField}
              isZasadna={updatedPripomienka.zasadna}
              error={getFieldErrorMessage('zasadna')}
            />
          )}
        </Col>
        <Col width="one-half">
          <div className="form-buttons-container">
            <ConfirmModal
              question="Naozaj si želáte zmeny uložiť?"
              cancelButtonText="Nie"
              confirmButton={(<Button buttonStyle="action" size="large">Áno</Button>)}
              openButton={(
                <LoaderButton
                  disabled={saveLoading}
                  buttonStyle="action"
                  type="button"
                  size="large"
                >
                  Uložiť
                </LoaderButton>
              )}
              doAfterConfirm={onSubmit}
            />
            <Button onClick={onReset} type="reset" buttonStyle="secondary" size="large">Zrušiť</Button>
          </div>
        </Col>
      </Row>
    </form>
  );
};

export default EditPripomienkaForm;
